import { useNavigate } from 'react-router';
import { useParams } from 'react-router-dom';
import { Form, Formik, FormikProps } from 'formik';
import * as Yup from 'yup';
import { Box, Button, Card, CardContent, Grid, Container } from '@mui/material';

// Hooks and services
import { useNotifyMessage } from '../../../hooks/useNotifyMessage';
import { resetAppUserPassword } from '../../../services/user.service';

// Types
import { ValidationErrorResponse } from '../../../utils/ValidationErrorResponse';
import { PasswordResetRequest } from '../../../requestModels/passwordResetRequest';

// Components
import SiteBranding from '../../base/branding/sitebranding';
import TextField from '../../common/forms/TextField';
import Footer from '../../base/layout/Footer';
import { tablePrimaryButtonSx } from '../../../styles/tableButtonStyles';

export default function AppUserPasswordReset() {
  const { notifyMessage } = useNotifyMessage();
  const { id, code } = useParams();
  const navigate = useNavigate();

  if (!id || !code) {
    // this will not happen, because the route won't match, but otherwise id and code will remain possibly undefined
    return <em>Id and code are both required</em>;
  }

  /*
  We could optionally already validate the code at this stage, so that we do not show the form when the
  user or the code is not found or the code is expired or already used. Now you need to send and fill out the form
  before you find out. I consider it a nice-to-have.
   */

  const handleSubmit = async (values) => {
    // 1. Send to backend
    const response = await resetAppUserPassword(id, code, values as PasswordResetRequest);

    if (response.status == 200) {
      // 2. Go to thank you page
      navigate('/password-reset-ok', { state: { email: values.email } });

      return;
    }

    if (response.status == 400) {
      const errorResponse = response.data as ValidationErrorResponse;
      let lowerCaseFirst: string;
      const errors: object = {};

      // backend validation errors
      if (errorResponse.errors) {
        for (const a in errorResponse.errors) {
          // lowercase first letter of fieldname
          lowerCaseFirst = a.charAt(0).toLowerCase() + a.slice(1);
          errors[lowerCaseFirst] = errorResponse.errors[a];
        }

        if (formRef && errors) {
          formRef.setErrors(errors);
        }
      }
    }

    if (response.status > 400) {
      // Not found, already used or expired
      notifyMessage(response.data.toString(), 'error');
    }
  };

  const validationSchema = Yup.object<PasswordResetRequest>({
    password: Yup.string().password().required('Password is required'),
    passwordConfirmation: Yup.string()
      .required('Please re-type the password')
      .oneOf([Yup.ref('password'), ''], 'Passwords must match'),
  });

  const initialValues = {
    password: '',
    passwordConfirmation: '',
  };
  let formRef: FormikProps<PasswordResetRequest>;
  return (
    <>
      <div className='app'>
        <main className='content'>
          <div className='content_body'>
            <Container maxWidth={'md'}>
              <Box display='flex' justifyContent='center' className='logoBox'>
                <Box display='flex'>
                  <SiteBranding className='logo' />
                </Box>
              </Box>
              <Box display='flex' justifyContent='center' className='logoBox'>
                <Box display='flex'>
                  <h1>Please type a new password</h1>
                </Box>
              </Box>
              <Box>
                <Card>
                  <CardContent>
                    <Formik initialValues={{ ...initialValues }} onSubmit={handleSubmit} validationSchema={validationSchema}>
                      {(props: FormikProps<PasswordResetRequest>) => (
                        <Form>
                          {props ? (formRef = props) && '' : ''}
                          <Grid container spacing={1}>
                            <Grid item xs={12}>
                              <TextField label={'Password'} type='password' required={true} name='password' />
                            </Grid>

                            <Grid item xs={12}>
                              <TextField label='Confirm password' type='password' required={true} name='passwordConfirmation' />
                            </Grid>

                            <Grid item xs={12}>
                              <Button type='submit' color='primary' variant='contained' size='small' sx={tablePrimaryButtonSx}>
                                Save new password
                              </Button>
                            </Grid>
                          </Grid>
                        </Form>
                      )}
                    </Formik>
                  </CardContent>
                </Card>
              </Box>
              <Footer />
            </Container>
          </div>
        </main>
      </div>
    </>
  );
}
